import React, { useEffect, useState } from 'react'
import { Stack, TextField, Typography } from '@mui/material'
import Image from 'next/image'
import { useSession } from 'next-auth/react'

import { TooltipCustom } from '#/shared'
import styles from '#/shared/styles/api-keys.module.scss'
import { getApiUrl } from '#/shared/lib/constants'
import { useShowDataStore } from '#/shared/lib/hooks/use-show-data'
import { InputStyleSmallDark } from '#/shared/ui/input'
import axios from 'axios'

const isValidLimit = (value: string) => value === '' || /^[1-9]\d*$/.test(value)

export type KeyItemBaseProps = {
	keyValue: string
	name: string
	limit: string | number | null
	createdAt: string
	expiresAt: string | null
	onDelete: (name: string) => void | Promise<void>
}

export type KeyItemRenderProps = {
	isCopy: boolean
	limit: string | number | null
	handleCopy: (text: string) => void
	handleLimitChange: (value: string) => void
}

type KeyItemProps = KeyItemBaseProps & {
	children: (renderProps: KeyItemRenderProps) => React.ReactNode
}

export const KeyItem: React.FC<KeyItemProps> = ({ children, limit: initialLimit, name }) => {
	const [isCopy, setIsCopy] = useState(false)
	const [limit, setLimit] = useState<string | number | null>(initialLimit)
	const { data: session } = useSession()
	const { showMessage } = useShowDataStore()

	useEffect(() => {
		setLimit(initialLimit)
	}, [initialLimit])

	const handleCopy = (text: string) => {
		if (typeof navigator === 'undefined' || !navigator.clipboard) {
			return
		}
		navigator.clipboard.writeText(text)
		setIsCopy(true)
		if (typeof window !== 'undefined') {
			window.setTimeout(() => setIsCopy(false), 3000)
		}
	}

	useEffect(() => {
		if (typeof window === 'undefined') {
			return
		}
		const timeout = window.setTimeout(() => {
			if (limit !== initialLimit) {
				if (!session?.access) {
					return
				}
				axios.patch(
					getApiUrl() + '/public/api-key',
					{
						token_limit: limit === '' ? null : Number(limit),
						name,
					},
					{ headers: { Authorization: `Bearer ${session.access}` } }
				)
			}
		}, 1000)

		return () => window.clearTimeout(timeout)
	}, [initialLimit, limit, name, session?.access])

	const handleLimitChange = (value: string) => {
		if (!isValidLimit(value)) {
			showMessage('Укажите целое число больше нуля')
			return
		}
		setLimit(value)
	}

	return (
		<>
			{children({
				isCopy,
				limit,
				handleCopy,
				handleLimitChange,
			})}
		</>
	)
}

type KeyItemDetailsProps = {
	title: string
	value: string | null
}

export const KeyItemDetail: React.FC<KeyItemDetailsProps> = ({ title, value }) => (
	<Stack spacing={0.5}>
		<Typography className='title-block'>{title}</Typography>
		<Typography className='text'>{value ?? '—'}</Typography>
	</Stack>
)

export const KeyItemLimitField: React.FC<{
	value: string | number | null
	onChange: (value: string) => void
	fullWidth?: boolean
}> = ({ value, onChange, fullWidth = false }) => (
	<TextField
		fullWidth={fullWidth}
		value={value ?? ''}
		placeholder={value === null || value === '' ? 'Бесконечно' : ''}
		type='text'
		onChange={(event) => onChange(event.target.value)}
		sx={{ ...InputStyleSmallDark }}
	/>
)

export const CopyButton: React.FC<{
	isCopy: boolean
	onCopy: () => void
}> = ({ isCopy, onCopy }) =>
	isCopy ? (
		<Image src={'/svg/tic.svg'} width={20} height={22} alt={'copy_success'} />
	) : (
		<TooltipCustom title={'Скопировать ключ'}>
			<Image
				onClick={onCopy}
				src={'/svg/copy.svg'}
				width={20}
				height={20}
				className={styles.clickableIcon}
				alt={'copy'}
			/>
		</TooltipCustom>
	)

